import React from 'react'

import { Icon, Tooltip, type TooltipProps } from '../../module'
import styles from './_form-label.module.scss'

export type FormLabelProps = {
  /** The string or element that will display for the label. */
  label: React.ReactNode
  /** Optional prop for a secondary label. This will appear to the right of the label. */
  rightLabel?: React.ReactNode
  /** Optional prop for a tooltip. This will appear as a blue info icon to the right of the label. */
  tooltip?: Omit<TooltipProps, 'children'>
  /** Optional prop to show that the field is required. A red asterisk will appear next to the label. */
  required?: boolean
  /** Optional prop to style the label when the corresponding field has an error. */
  error?: boolean
  /** Optional prop to style the label when the corresponding field is disabled. */
  disabled?: boolean
  /** Optional prop to allow an id to be passed in. This will allow the form element to be selected when clicking on the label. */
  id?: string
  /** Optional prop to add a test id to the FormLabel for QA testing */
  qaTestId?: string
}

const FormLabel = ({
  label,
  rightLabel,
  tooltip,
  required,
  error,
  id,
  disabled,
  qaTestId = 'form-label',
}: FormLabelProps): React.JSX.Element => {
  return (
    <label
      className={`${styles.label} ${
        error ? styles.error : ''
      } ${disabled ? styles.disabled : ''}`.trim()}
      htmlFor={id}
      data-testid='form-label'
      qa-test-id={qaTestId}
    >
      <div className={styles.labelContainer}>
        <span>
          {label}
          {required && <span className={styles.requiredAsterisk}>*</span>}
        </span>
        {tooltip && (
          <Tooltip position='right' {...tooltip}>
            <Icon icon='info' color='dark-blue' iconSize='12px' />
          </Tooltip>
        )}
      </div>
      {rightLabel && <div>{rightLabel}</div>}
    </label>
  )
}

export default FormLabel
